import React, { useState } from 'react'

import { GlobalColorTag } from 'services/apiTypes/colorTag.types'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import ColorTagEditModal from 'components/misc/ColorTagEditModal'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'

import { getContrastingColor } from 'utils/calculateContrast'

import { defaultDeckColorTags } from 'redux/deck/initialState'

import styles from './colorTagTable.module.scss'
import { SimpleButton } from 'components/formElements/Button'

type Props = {
  tags: GlobalColorTag[]

  onEdited?: (tag: GlobalColorTag) => void
  onSetAll?: (tag: GlobalColorTag) => void
  onRemovedFromDeck?: (tag: GlobalColorTag) => void
  onAddToDeck?: (tag: GlobalColorTag) => void

  onDeleted?: (tag: GlobalColorTag) => Promise<void> // Promisified varient for network removing global color tags vs locally removing color tags from decks

  noEditOnDefaults?: boolean
  usedInOverlay?: boolean
  usedOnDeckPage?: boolean
}

const ColorTagTable = ({
  tags,
  onEdited,
  onDeleted,
  onAddToDeck,
  onRemovedFromDeck,
  onSetAll,
  noEditOnDefaults = false,
  usedInOverlay = false,
  usedOnDeckPage = false,
}: Props) => {
  const [editingColorTag, setEditingColorTag] = useState<GlobalColorTag | null>(null)
  const [deletingColorTag, setDeletingColorTag] = useState<GlobalColorTag | null>(null)
  const [changeAllToTag, setChangeAllToTag] = useState<GlobalColorTag | null>(null)

  const generateLookupKey = (tag: GlobalColorTag) => `${tag.name},${tag.color}`
  const nonEditableTags: Record<string, boolean> = defaultDeckColorTags.reduce(
    (acc, tag) => ({ ...acc, [generateLookupKey(tag)]: true }),
    {},
  )

  return (
    <>
      <table className={styles.list}>
        <thead>
          <tr>
            <th>Tag name</th>
            <th>Color</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tags.map((tag, index) => (
            <tr key={index}>
              <td style={tag.name === '' ? { fontStyle: 'italic' } : {}}>{tag.name || 'Default'}</td>
              <td className={styles.colorCell}>
                <div
                  className={styles.colorTag}
                  style={{ backgroundColor: tag.color, color: getContrastingColor(tag.color) }}>
                  {tag.color.toLocaleUpperCase()}
                </div>
              </td>

              <td>
                {/* If we have no options, don't bother showing the dropdown at all */}
                {(onSetAll || onDeleted || onEdited) && (
                  <ArchidektDropdown
                    menuClassName={styles.dropdown}
                    options={[
                      {
                        icon: 'arrow right',
                        label: 'Set all',
                        onClick: () => setChangeAllToTag(tag),
                        hidden: !onSetAll,
                      },
                      {
                        icon: 'edit',
                        label: 'Edit',
                        onClick: () => setEditingColorTag(tag),
                        hidden: !onEdited || (noEditOnDefaults && nonEditableTags[generateLookupKey(tag)]),
                      },
                      {
                        icon: 'trash alternate outline',
                        label: 'Delete',
                        onClick: () => setDeletingColorTag(tag),
                        hidden: !onDeleted,
                      },
                      {
                        icon: 'trash alternate outline',
                        label: 'Remove from deck',
                        onClick: () => setDeletingColorTag(tag),
                        hidden: !onRemovedFromDeck || (noEditOnDefaults && nonEditableTags[generateLookupKey(tag)]),
                      },
                    ]}
                  />
                )}
                {onAddToDeck && (
                  <SimpleButton className={styles.addCircle} onClick={() => onAddToDeck(tag)} icon="add" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ColorTagEditModal
        noEditName={usedOnDeckPage}
        usedInOverlay={usedInOverlay}
        colorTag={editingColorTag}
        open={editingColorTag !== null}
        onClose={() => setEditingColorTag(null)}
        onEdit={colorTag => {
          if (!onEdited) return

          onEdited(colorTag)
          setEditingColorTag(null)
        }}
      />

      {onDeleted && (
        <ConfirmDeleteModal
          usedInOverlay={usedInOverlay}
          open={deletingColorTag !== null}
          onClose={() => setDeletingColorTag(null)}
          onDelete={() => {
            if (!deletingColorTag) return Promise.resolve()

            return onDeleted(deletingColorTag).then(() => setDeletingColorTag(null))
          }}
          // onConfirm={!onDeleted && onRemoved && deletingColorTag ? () => onRemoved(deletingColorTag) : undefined}
          header="Delete color tag"
          message={`Are you sure you want to delete the color tag "${deletingColorTag?.name}?`}
          buttonText="Delete color tag"
          loadingMessage="Deleting color tag..."
        />
      )}

      {onRemovedFromDeck && (
        <ConfirmDeleteModal
          usedInOverlay={usedInOverlay}
          open={deletingColorTag !== null}
          onClose={() => setDeletingColorTag(null)}
          onConfirm={() => {
            if (!deletingColorTag) return

            onRemovedFromDeck(deletingColorTag)
            setDeletingColorTag(null)
          }}
          header="Remove color tag"
          message={`Are you sure you want to remove the color tag "${deletingColorTag?.name}"? Doing so will set all cards with this color tag to the default color tag.`}
          buttonText="Remove color tag"
          loadingMessage="Removing color tag..."
        />
      )}
      {onSetAll && (
        <ConfirmDeleteModal
          usedInOverlay={usedInOverlay}
          open={changeAllToTag !== null}
          onClose={() => setChangeAllToTag(null)}
          onConfirm={() => {
            if (!changeAllToTag) return

            onSetAll(changeAllToTag)
            setChangeAllToTag(null)
          }}
          header="Set all cards to this color tag"
          message="Are you sure you want to set all cards to this color tag?"
          buttonText={`Set all to "${changeAllToTag?.name || 'Default color tag'}"`}
        />
      )}
    </>
  )
}

export default ColorTagTable
